<template>
  <div class="app">

    <header class="header">
      <div class="header-inner">
        <div class="brand">
          <span class="brand-name">IRIS CERAMICA</span>
          <span class="brand-divider"></span>
          <span class="brand-sub">Display Control</span>
        </div>
        <div class="header-right">
          <a href="https://we.tl/t-1tBmEweupg" target="_blank" rel="noopener" class="download-btn">
            Download Android App
          </a>
          <div class="poc-tag">
            <img src="/logo/logo-mumble-blu.svg" alt="Mumble" class="mumble-logo" />
            <span class="poc-label">Proof of Concept</span>
          </div>
        </div>
      </div>
    </header>

    <main class="main">

      <div class="page-intro">
        <h1 class="page-title">Select Display Content</h1>
        <p class="page-desc">Choose the media to broadcast on the Android TV display.</p>
      </div>

      <div v-if="loading" class="state-container">
        <div class="loader"></div>
      </div>

      <div v-else-if="error" class="state-container">
        <p class="state-message">{{ error }}</p>
        <button @click="loadMedia" class="btn-retry">Try again</button>
      </div>

      <template v-else>

        <div v-if="videos.length" class="section">
          <div class="section-header">
            <span class="section-title">Video</span>
            <span class="section-count">{{ videos.length }}</span>
          </div>
          <div class="gallery">
            <div
              v-for="media in videos"
              :key="media.filename"
              class="gallery-item"
              :class="{ 'is-active': selectedMedia === media.url, 'is-selecting': selectingMedia === media.url }"
              @click="selectMedia(media)"
            >
              <div class="gallery-media">
                <video
                  :src="media.url"
                  muted
                  loop
                  playsinline
                  @mouseenter="playVideo"
                  @mouseleave="pauseVideo"
                ></video>
                <div class="gallery-overlay">
                  <div v-if="selectedMedia === media.url" class="check-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8L6.5 11.5L13 4.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="images.length" class="section">
          <div class="section-header">
            <span class="section-title">Images</span>
            <span class="section-count">{{ images.length }}</span>
          </div>
          <div class="gallery">
            <div
              v-for="media in images"
              :key="media.filename"
              class="gallery-item"
              :class="{ 'is-active': selectedMedia === media.url, 'is-selecting': selectingMedia === media.url }"
              @click="selectMedia(media)"
            >
              <div class="gallery-media">
                <img
                  :src="previewUrl(media.url)"
                  :alt="media.filename"
                  loading="lazy"
                  @load="onImageLoad($event, media)"
                />
                <div class="gallery-overlay">
                  <div v-if="selectedMedia === media.url" class="check-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8L6.5 11.5L13 4.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <span v-if="orientations[media.filename]" class="orientation-tag">
                  {{ orientations[media.filename] }}
                </span>
              </div>
            </div>
          </div>
        </div>

      </template>

    </main>

    <footer class="footer">
      <div class="footer-inner">
        <span class="footer-copy">Made with ♥ by <a href="https://mumble.it" target="_blank" rel="noopener" class="footer-link">Mumble</a></span>
        <span class="footer-divider"></span>
        <span class="footer-poc">This is a Proof of Concept — not intended for production use.</span>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: 'MediaSelector',
  data() {
    return {
      mediaList: [],
      selectedMedia: null,
      selectingMedia: null,
      loading: true,
      error: null,
      baseUrl: window.location.origin,
      orientations: {}
    };
  },
  computed: {
    videos() {
      return this.mediaList.filter(m => m.type === 'video');
    },
    images() {
      return this.mediaList.filter(m => m.type === 'image');
    }
  },
  mounted() {
    this.loadMedia();
  },
  methods: {
    async loadMedia() {
      this.loading = true;
      this.error = null;
      try {
        const response = await fetch('/api/media-list');
        const data = await response.json();
        this.mediaList = data.media || [];
        if (this.mediaList.length === 0) {
          this.error = 'No media available.';
        }
      } catch (err) {
        console.error('Error loading media:', err);
        this.error = 'Could not load media.';
      } finally {
        this.loading = false;
      }
    },

    async selectMedia(media) {
      if (this.selectingMedia) return;
      this.selectingMedia = media.url;
      try {
        const response = await fetch('/api/select-media', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: media.url })
        });
        if (response.ok) {
          this.selectedMedia = media.url;
        } else {
          throw new Error('Failed to select media');
        }
      } catch (err) {
        console.error('Error selecting media:', err);
      } finally {
        this.selectingMedia = null;
      }
    },

    previewUrl(url) {
      return `/_vercel/image?url=${encodeURIComponent(url)}&w=640&q=75`;
    },

    onImageLoad(event, media) {
      const { naturalWidth, naturalHeight } = event.target;
      if (naturalWidth && naturalHeight) {
        this.orientations = {
          ...this.orientations,
          [media.filename]: naturalWidth >= naturalHeight ? 'Landscape' : 'Portrait'
        };
      }
    },

    playVideo(event) {
      if (event.target.tagName === 'VIDEO') {
        event.target.play().catch(() => {});
      }
    },

    pauseVideo(event) {
      if (event.target.tagName === 'VIDEO') {
        event.target.pause();
      }
    }
  }
};
</script>

<style scoped>
*, *::before, *::after {
  box-sizing: border-box;
}

.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9f8f6;
  color: #1a1a1a;
  font-family: 'Georgia', serif;
}

/* ── Header ── */
.header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f9f8f6;
  border-bottom: 1px solid #e0ddd8;
}

.header-inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 1.75rem 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.brand-name {
  font-family: 'Georgia', serif;
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  color: #1a1a1a;
}

.brand-divider {
  display: inline-block;
  width: 1px;
  height: 16px;
  background: #c8c4bd;
}

.brand-sub {
  font-family: 'Georgia', serif;
  font-size: 1rem;
  letter-spacing: 0.1em;
  color: #8a8680;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.download-btn {
  font-family: -apple-system, sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f9f8f6;
  background: #1a1a1a;
  padding: 0.625rem 1.25rem;
  text-decoration: none;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.download-btn:hover {
  opacity: 0.75;
}

.poc-tag {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
}

.mumble-logo {
  height: 16px;
  width: auto;
  opacity: 0.7;
}

.poc-label {
  font-family: -apple-system, sans-serif;
  font-size: 0.625rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8680;
  line-height: 1;
}

/* ── Main ── */
.main {
  flex: 1;
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: 5rem 3rem 4rem;
}

.page-intro {
  margin-bottom: 4rem;
  border-left: 2px solid #1a1a1a;
  padding-left: 1.5rem;
}

.page-title {
  font-family: 'Georgia', serif;
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  margin: 0 0 0.5rem;
  color: #1a1a1a;
}

.page-desc {
  font-family: -apple-system, sans-serif;
  font-size: 0.875rem;
  color: #8a8680;
  margin: 0;
  letter-spacing: 0.01em;
}

/* ── States ── */
.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8rem 1rem;
  gap: 2rem;
}

.loader {
  width: 32px;
  height: 32px;
  border: 1px solid #e0ddd8;
  border-top-color: #1a1a1a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.state-message {
  font-family: -apple-system, sans-serif;
  font-size: 0.875rem;
  color: #8a8680;
  letter-spacing: 0.04em;
}

.btn-retry {
  font-family: -apple-system, sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: #1a1a1a;
  color: #f9f8f6;
  border: none;
  padding: 0.75rem 2rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-retry:hover {
  opacity: 0.75;
}

/* ── Sections ── */
.section {
  margin-bottom: 4rem;
}

.section-header {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e0ddd8;
}

.section-title {
  font-family: 'Georgia', serif;
  font-size: 0.875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1a1a1a;
}

.section-count {
  font-family: -apple-system, sans-serif;
  font-size: 0.75rem;
  color: #8a8680;
}

/* ── Gallery ── */
.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2px;
}

.gallery-item {
  cursor: pointer;
  position: relative;
}

.gallery-media {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #e8e5e0;
}

.gallery-media img,
.gallery-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.gallery-item:hover .gallery-media img,
.gallery-item:hover .gallery-media video {
  transform: scale(1.04);
}

.gallery-overlay {
  position: absolute;
  inset: 0;
  background: rgba(26, 26, 26, 0);
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.gallery-item:hover .gallery-overlay {
  background: rgba(26, 26, 26, 0.2);
}

.gallery-item.is-active .gallery-overlay {
  background: rgba(26, 26, 26, 0.35);
}

.gallery-item.is-selecting .gallery-overlay {
  background: rgba(26, 26, 26, 0.15);
}

.check-icon {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(4px);
}

.gallery-item.is-active {
  outline: 2px solid #1a1a1a;
  outline-offset: -2px;
}

.orientation-tag {
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  font-family: -apple-system, sans-serif;
  font-size: 0.5625rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
}

/* ── Footer ── */
.footer {
  border-top: 1px solid #e0ddd8;
  background: #f9f8f6;
  margin-top: auto;
}

.footer-inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 1.25rem 3rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.footer-copy {
  font-family: -apple-system, sans-serif;
  font-size: 0.75rem;
  color: #8a8680;
  white-space: nowrap;
}

.footer-link {
  color: #1a1a1a;
  text-decoration: none;
}

.footer-link:hover {
  text-decoration: underline;
}

.footer-divider {
  display: inline-block;
  width: 1px;
  height: 12px;
  background: #c8c4bd;
  flex-shrink: 0;
}

.footer-poc {
  font-family: -apple-system, sans-serif;
  font-size: 0.75rem;
  color: #b0aca6;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .header-inner {
    padding: 1rem 1.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .brand {
    flex: 1;
    min-width: 0;
  }

  .header-right {
    width: 100%;
    justify-content: space-between;
    gap: 1rem;
  }

  .download-btn {
    font-size: 0.6875rem;
    padding: 0.5rem 1rem;
  }

  .poc-tag {
    align-items: flex-end;
  }

  .main {
    padding: 3rem 1.5rem 3rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .gallery {
    grid-template-columns: repeat(2, 1fr);
  }

  .footer-inner {
    padding: 1rem 1.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .footer-divider {
    display: none;
  }
}

@media (max-width: 480px) {
  .brand-divider,
  .brand-sub {
    display: none;
  }
}

@media (max-width: 480px) {
  .gallery {
    grid-template-columns: 1fr;
  }
}
</style>
